import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin } from 'lucide-react';
import { useCMSContent } from '../hooks/useCMSContent';

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  // Fetch CMS content
  const { data: companyInfo } = useCMSContent('companyInfo');
  const { data: services } = useCMSContent('services');

  return (
    <footer style={{
      backgroundColor: 'var(--bg-section-dark)',
      borderTop: '1px solid var(--border-medium)',
      marginTop: 'auto'
    }} data-testid="footer">
      <div className="container" style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '60px 40px 40px'
      }}>
        {/* Footer Content Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '40px',
          marginBottom: '40px'
        }}>
          {/* Company Info */}
          <div>
            <h3 style={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '1.25rem',
              color: 'white',
              marginBottom: '20px'
            }}>
              {companyInfo?.companyName || 'Laurel Run Management Group'}
            </h3>
            <p style={{
              color: '#b8c5d3',
              fontSize: '0.95rem',
              lineHeight: 1.6,
              marginBottom: '20px'
            }}>
              {companyInfo?.tagline || 'Professional Property Management You Can Trust'}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <a href={`mailto:${companyInfo?.email || 'Office@lrmgmt.net'}`} style={{
                color: 'white',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                fontSize: '0.95rem',
                transition: 'color 0.3s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#60a5fa'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'white'}
              >
                <Mail size={18} />
                {companyInfo?.email || 'Office@lrmgmt.net'}
              </a>
              <div style={{
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                fontSize: '0.95rem'
              }}>
                <Phone size={18} />
                {companyInfo?.phone || '(606) 683-3100'}
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 style={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: '1rem',
              color: 'white',
              marginBottom: '20px'
            }}>
              Quick Links
            </h4>
            <nav style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[
                { path: '/services', label: 'Property Management Services' },
                { path: '/owners', label: 'For Property Owners' },
                { path: '/tenants', label: 'For Tenants' },
                { path: '/about', label: 'About Us' },
                { path: '/contact', label: 'Contact' }
              ].map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  style={{
                    color: '#b8c5d3',
                    textDecoration: 'none',
                    fontSize: '0.95rem',
                    transition: 'color 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.color = '#60a5fa'}
                  onMouseLeave={(e) => e.target.style.color = '#b8c5d3'}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Services */}
          <div>
            <h4 style={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: '1rem',
              color: 'white',
              marginBottom: '20px'
            }}>
              Our Services
            </h4>
            <ul style={{ 
              listStyle: 'none', 
              padding: 0, 
              margin: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              {(services || []).slice(0, 4).map((service, index) => (
                <li key={service.id || service.documentId || index} style={{
                  color: '#b8c5d3',
                  fontSize: '0.95rem'
                }}>
                  {service.title}
                </li>
              ))}
            </ul>
          </div>

          {/* Business Hours */}
          <div>
            <h4 style={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: '1rem',
              color: 'white',
              marginBottom: '20px'
            }}>
              Business Hours
            </h4>
            <p style={{
              color: '#b8c5d3',
              fontSize: '0.95rem',
              lineHeight: 1.8
            }}>
              {companyInfo?.businessHours || 'Monday - Friday: 9:00 AM - 5:00 PM'}
            </p>
            <div style={{
              marginTop: '20px',
              padding: '15px',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <p style={{
                color: '#b8c5d3',
                fontSize: '0.85rem',
                margin: 0,
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px'
              }}>
                <MapPin size={16} style={{ marginTop: '2px', flexShrink: 0 }} />
                <span>Professional property management services available throughout the region</span>
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div style={{
          paddingTop: '30px',
          borderTop: '1px solid var(--border-light)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '20px'
        }}>
          <p style={{
            color: '#b8c5d3',
            fontSize: '0.9rem',
            margin: 0
          }}>
            © {currentYear} {companyInfo?.companyName || 'Laurel Run Management Group'}. All rights reserved.
          </p>
          <div style={{ display: 'flex', gap: '20px' }}>
            <a href="#" style={{
              color: '#b8c5d3',
              textDecoration: 'none',
              fontSize: '0.9rem',
              transition: 'color 0.3s ease'
            }}
            onMouseEnter={(e) => e.target.style.color = '#60a5fa'}
            onMouseLeave={(e) => e.target.style.color = '#b8c5d3'}
            >
              Privacy Policy
            </a>
            <a href="#" style={{
              color: '#b8c5d3',
              textDecoration: 'none',
              fontSize: '0.9rem',
              transition: 'color 0.3s ease'
            }}
            onMouseEnter={(e) => e.target.style.color = '#60a5fa'}
            onMouseLeave={(e) => e.target.style.color = '#b8c5d3'}
            >
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};
